import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  ArrowUpRight, Zap, Store, Package, Heart, Percent, Calendar, Bookmark,
  BarChart3, Clock, TrendingUp, Cpu, Users, ShoppingBag, Sparkles, CheckCircle2,
  ChevronRight, Ticket, Gem, Landmark,
} from "lucide-react";

function PlanSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/plan").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const { plan, banco } = data;
  return (
    <section id="plan" data-testid="plan-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="bg-[#FF3366] text-white rounded-sm p-8 flex flex-col">
          <div className="overline text-white/80 flex items-center gap-1"><Gem className="w-3.5 h-3.5" /> Un solo plan, todo incluido</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-2">{plan.nombre}</div>
          <div className="mt-6 font-display font-black text-6xl tracking-tighter leading-none" data-testid="plan-precio">${plan.precio.toFixed(0)}<span className="text-xl font-medium"> {plan.moneda}</span></div>
          <div className="text-sm text-white/85 mt-1">por {plan.dias} días · sin permanencia · sin comisiones ocultas</div>
          <Link to="/register" className="mt-8"><Button data-testid="plan-cta" className="w-full h-12 rounded-sm bg-zinc-900 hover:bg-white hover:text-zinc-900 font-display font-bold">Registrar mi comercio <ArrowUpRight className="w-4 h-4 ml-1" /></Button></Link>
          <div className="mt-6 bg-white/10 rounded-sm p-4 text-xs">
            <div className="font-bold flex items-center gap-1 mb-2"><Landmark className="w-3.5 h-3.5" /> Activación por transferencia</div>
            <div>{banco.titular}</div><div>{banco.banco} · CLABE <span className="font-mono">{banco.clabe}</span></div>
            <div className="text-white/70 mt-2">Sube tu comprobante desde tu panel y el administrador activa tu plan.</div>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-8">
          <div className="overline text-zinc-500">Beneficios para tu comercio</div>
          <h2 className="font-display font-black text-3xl tracking-tighter mt-1">Todo lo que necesitas para vender más, en un solo lugar</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plan.beneficios.map((b, i) => (
              <div key={b.t} data-testid={`beneficio-${i}`} className="border border-zinc-200 rounded-sm p-4 card-lift">
                <CheckCircle2 className="w-5 h-5 text-[#FF3366]" />
                <div className="font-display font-bold tracking-tight mt-2">{b.t}</div>
                <div className="text-xs text-zinc-600 mt-1">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const MODULOS = [
  { icon: Store, title: "Marketplace de la plaza", desc: "Tu tienda física ahora vende online 24/7 dentro del catálogo unificado de tu mall.",
    color: "#FF3366", img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=900&q=80" },
  { icon: Zap, title: "Entrega en menos de 2 horas", desc: "Repartidores propios del mall llevan tu producto en tiempo récord. Compites contra Amazon.",
    color: "#D4FF00", img: "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=900&q=80" },
  { icon: Package, title: "Compra en línea, recoge en tienda", desc: "Modelo BOPIS integrado: el cliente paga desde su sofá y pasa a recoger sin filas.",
    color: "#7C3AED", img: "https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=900&q=80" },
  { icon: Heart, title: "Programa de lealtad compartido", desc: "Un solo programa de puntos válido en TODOS los locales de la plaza. Retención masiva.",
    color: "#F59E0B", img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80" },
  { icon: Percent, title: "Promociones cross-tienda", desc: "Descuentos combinados entre locales. Un cliente compra en 3 tiendas por el 20% extra.",
    color: "#10B981", img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=900&q=80" },
  { icon: Calendar, title: "Eventos de la plaza", desc: "Ferias, lanzamientos y activaciones. Tu tienda invitada automáticamente al calendario.",
    color: "#3B82F6", img: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=900&q=80" },
  { icon: Bookmark, title: "Reservación de productos", desc: "El cliente aparta su talla desde el móvil y tiene 4 horas para pasar a probarla.",
    color: "#EC4899", img: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=900&q=80" },
  { icon: BarChart3, title: "Analítica para la administración", desc: "Dashboard en vivo: ventas por hora, productos ganadores, mapas de calor por local.",
    color: "#0EA5E9", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80" },
];

const METRICAS = [
  { valor: "+38%", label: "ventas entre semana" },
  { valor: "-72%", label: "días muertos" },
  { valor: "< 2 hrs", label: "tiempo de entrega" },
  { valor: "1 solo", label: "envío para el cliente" },
];

const PASOS = [
  { n: "01", title: "Registras tu local", desc: "En 5 minutos subes productos, stock y fotos. Solo necesitas tu celular.",
    img: "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?w=900&q=80" },
  { n: "02", title: "Tu producto aparece en el catálogo del mall", desc: "Miles de compradores hiperlocales lo ven mientras tú atiendes en piso.",
    img: "https://images.unsplash.com/photo-1522204523234-8729aa6e3d5f?w=900&q=80" },
  { n: "03", title: "Se paga y empacas", desc: "La plataforma recibe el pago seguro. Marcas 'Listo' y sale el repartidor, o envías por Uber / paquetería foránea.",
    img: "https://images.unsplash.com/photo-1607082349566-187342175e2f?w=900&q=80" },
  { n: "04", title: "Cliente feliz, tú vendes más", desc: "Producto entregado en < 2 horas. Reseña automática. Cliente vuelve.",
    img: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?w=900&q=80" },
];

export default function ParaLocatarios() {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar transparent />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#FF3366]/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D4FF00]/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-20">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 overline text-zinc-600 bg-white border border-zinc-200 px-3 py-1.5 rounded-full">
              <Cpu className="w-3 h-3" /> Cualquier comercio en México · Barrio, tienda o plaza
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-[88px] leading-[0.9] tracking-tighter max-w-5xl"
          >
            Vendemos por ti.<br />
            <span className="italic font-black bg-[#D4FF00] px-3 -rotate-1 inline-block">en 90 minutos.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-lg lg:text-xl text-zinc-600 max-w-2xl font-light"
          >
            No importa si estás en una plaza comercial, en una tienda de barrio o vendes desde tu casa: cualquier comercio en México puede sumarse. Le competimos a <span className="text-zinc-900 font-medium">Amazon, eBay y Mercado Libre</span> porque entregamos en menos de 90 minutos, no en 3 días. Además damos ingresos extras a miles de <b>NENIS</b> y <b>repartidores</b> en todo el país.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link to="/register" data-testid="hero-register-tienda">
              <Button className="h-12 px-6 rounded-sm bg-zinc-900 hover:bg-[#FF3366] text-white font-medium">
                Registrar mi local <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <a href="#modulos">
              <Button variant="outline" className="h-12 px-6 rounded-sm border-zinc-300">
                Ver todos los módulos
              </Button>
            </a>
            <div className="ml-2 text-xs text-zinc-500">PLAN INTEGRAL · $299 MXN / 30 días · Sin permanencia</div>
          </motion.div>

          {/* Marketing con cupones — hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            data-testid="hero-cuponeras"
            className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6 bg-zinc-900 text-white rounded-sm p-8 lg:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#FF3366]/30 rounded-full blur-3xl" />
            <div className="relative">
              <div className="overline text-[#D4FF00] flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> Marketing con CUPONERAS · Incluido en tu plan</div>
              <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tighter leading-[0.95]">Construye tu base de datos de clientes y tráelos de regreso con cupones.</h2>
              <p className="mt-4 text-white/75 text-sm lg:text-base">Cada cliente que escanea tu QR queda registrado con su celular y código postal. Ya no dependes de que pase por tu calle: le envías descuentos diseñados por IA directo a su celular, y tus cupones también llegan a los clientes de otros comercios afiliados cerca de ti.</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm">
                {["Hasta 5 cupones al mes diseñados por IA", "QR único para imprimir en tu mostrador", "Cuponera instalable en el celular del cliente", "Canje con código de 6 letras, sin fraudes"].map(t => (
                  <li key={t} className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-[#D4FF00] shrink-0 mt-0.5" /> {t}</li>
                ))}
              </ul>
            </div>
            <ol className="relative grid gap-2 text-sm self-center">
              {[
                ["Imprime tu QR", "Lo generamos único para tu comercio; pégalo en mostrador, puerta o bolsas."],
                ["El cliente escanea", "Se instala su Cuponera con solo celular y C.P. Tú ganas un contacto."],
                ["Diseñas con IA", "Describes producto, descuento, vigencia y estilo; la IA crea el cupón."],
                ["Ellos regresan", "Reclaman el cupón, te muestran el código y tú lo canjeas. Venta cerrada."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3 bg-white/5 rounded-sm p-3"><span className="font-display font-black text-[#FF3366] text-xl">{String(i + 1).padStart(2, "0")}</span><span><b>{t}.</b> <span className="text-white/70">{d}</span></span></li>
              ))}
            </ol>
          </motion.div>

          {/* Métricas */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {METRICAS.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white border border-zinc-200 rounded-sm p-6"
              >
                <div className="font-display font-black text-4xl lg:text-5xl tracking-tighter" style={{ color: i % 2 === 0 ? "#FF3366" : "#18181B" }}>
                  {m.valor}
                </div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2 font-medium">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Killer comparison */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-zinc-900 text-white rounded-sm p-10 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-56 h-56 bg-white/5 rounded-full blur-2xl" />
            <div className="overline text-white/50">Amazon / Mercado Libre</div>
            <div className="mt-3 font-display font-black text-4xl tracking-tighter">Te restan liquidez operativa</div>
            <ul className="mt-6 space-y-2 text-white/70 text-sm">
              <li>× Guardan tu dinero 14 días</li>
              <li>× El cliente compara con 200 sellers</li>
              <li>× Comisión 15-25%</li>
              <li>× Sin marca, sin lealtad</li>
            </ul>
          </div>
          <div className="bg-[#D4FF00] rounded-sm p-10 relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-black/5 rounded-full blur-2xl" />
            <div className="overline text-black/60">en 90 MINUTOS</div>
            <div className="mt-3 font-display font-black text-4xl tracking-tighter text-black">Entrega en 2 horas</div>
            <ul className="mt-6 space-y-2 text-zinc-900 text-sm font-medium">
              <li>✓ Tienda online propia (hasta 30 artículos) + 5 en el e-commerce general</li>
              <li>✓ Cliente compra en TU mall, no compara</li>
              <li>✓ Comisión SaaS baja + envío del mall</li>
              <li>✓ Tu marca, tu vitrina digital</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Módulos - Sistema Operativo */}
      <section id="modulos" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="overline text-zinc-500 mb-2">Sistema operativo</div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter max-w-3xl">
              8 módulos <span className="text-[#FF3366]">plug-and-play</span> para tu mall
            </h2>
          </div>
          <p className="text-zinc-600 max-w-md text-sm">Todo lo que un centro comercial digital necesita. Un solo login, una sola plataforma, una sola vitrina para todos tus locales.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {MODULOS.map((m, i) => <ModuloCard key={i} {...m} index={i} />)}
        </div>
      </section>

      {/* Cómo se ve un día exitoso — Process reveal */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-zinc-500 mb-2">Un día en en 90 MINUTOS</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-2xl">
            Así se ve una venta <span className="italic text-[#FF3366]">exitosa.</span>
          </h2>

          <div className="mt-14 space-y-24">
            {PASOS.map((p, i) => <PasoRow key={i} {...p} reverse={i % 2 === 1} />)}
          </div>
        </div>
      </section>

      {/* PLAN INTEGRAL */}
      <PlanSection />

      {/* Big CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="relative overflow-hidden rounded-sm bg-zinc-900 text-white p-12 lg:p-20">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#FF3366]/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-[#D4FF00]/30 rounded-full blur-3xl" />
          <div className="relative max-w-3xl">
            <Sparkles className="w-10 h-10 text-[#D4FF00]" />
            <h2 className="mt-6 font-display font-black text-4xl lg:text-6xl tracking-tighter">
              Tu producto en el<br />bolsillo del cliente<br />en <span className="text-[#D4FF00]">120 minutos.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-lg">
              Regístrate hoy. Vendemos por ti mientras atiendes tu piso.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="cta-final-register">
                <Button className="h-12 px-6 rounded-sm bg-[#FF3366] hover:bg-[#E62958] text-white font-medium">
                  Registrar mi local gratis <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/registrar-plaza">
                <Button variant="outline" className="h-12 px-6 rounded-sm bg-white/5 backdrop-blur border-white/30 text-white hover:bg-white/10 hover:text-white">
                  Soy dueño de un mall
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-white/50">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#D4FF00]" /> Sin permanencia</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#D4FF00]" /> Envíos por Uber y foráneos</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#D4FF00]" /> Onboarding en 5 min</span>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="font-display font-black text-2xl tracking-tight">en 90<span className="text-[#FF3366]"> MINUTOS</span></div>
          <div className="text-xs text-zinc-500">© 2026 · Sistema operativo para centros comerciales</div>
        </div>
      </footer>
    </div>
  );
}

function ModuloCard({ icon: Icon, title, desc, color, img, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      className="group card-lift bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col"
      data-testid={`modulo-${index}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
        <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-3 left-3 w-9 h-9 rounded-sm grid place-items-center" style={{ background: color }}>
          <Icon className={`w-4 h-4 ${["#D4FF00", "#F59E0B"].includes(color) ? "text-black" : "text-white"}`} />
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="font-display font-bold text-lg tracking-tight leading-tight">{title}</div>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed flex-1">{desc}</p>
        <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-zinc-900 group-hover:text-[#FF3366] transition-colors">
          Incluido <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </motion.div>
  );
}

function PasoRow({ n, title, desc, img, reverse }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}
      className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      <div>
        <div className="font-display font-black text-8xl tracking-tighter text-zinc-100 leading-none">{n}</div>
        <div className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tighter">{title}</div>
        <p className="mt-4 text-zinc-600 text-lg max-w-md leading-relaxed">{desc}</p>
      </div>
      <div className="relative aspect-[4/3] rounded-sm overflow-hidden bg-zinc-100">
        <img src={img} alt={title} className="w-full h-full object-cover" />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-sm px-3 py-1.5 text-xs font-mono font-bold">{n}</div>
      </div>
    </motion.div>
  );
}
